import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { RELAY_FAX_TRANSFERRED_RESOLUTIONS } from '../constants/nonRoutableRequests';
import useNonRoutableRelayFaxDetail from '../hooks/useNonRoutableRelayFaxDetail';
import NonRoutableRelayFaxSummary from './NonRoutableRelayFaxSummary';
import NonRoutableResolutionOutcome from './NonRoutableResolutionOutcome';
import RelayEventTimeline from './RelayEventTimeline';
import RelayLineItemsTable from './RelayLineItemsTable';

/**
 * POM-6279 — one parked RELAY FAX in full, read-only.
 *
 * SELF-CONTAINED, WITH NO LINK TO THE RELAY TAB. A parked fax's relay order is PENDING, and the
 * Relay tab lists TRANSFERRED orders only, so a link would land on a list that does not contain it.
 * Once the fax is delivered or reconciled the order IS transferred, and the view says so in plain
 * text rather than linking.
 *
 * The actions stay on the row menu, where the server's `availableActions` decide them; this view is
 * what the operator reads before choosing one.
 */
const NonRoutableRelayFaxDetailDialog = ({ open, onClose, item }) => {
  const { loading, detail, described } = useNonRoutableRelayFaxDetail({
    nonRoutableRequestId: item.nonRoutableRequestId,
    open,
  });

  const renderBody = () => {
    if (loading || (detail === undefined && !described)) {
      return <Skeleton variant='rectangular' height={240} data-testid='relay-fax-detail-loading' />;
    }

    if (described) {
      return <NonRoutableResolutionOutcome outcome={{ described }} />;
    }

    if (detail === null) {
      return (
        <Alert severity='info' data-testid='relay-fax-detail-missing'>
          This request is not a parked relay fax, or it no longer exists.
        </Alert>
      );
    }

    return (
      <>
        {RELAY_FAX_TRANSFERRED_RESOLUTIONS.has(detail.request.resolutionType) && (
          <Alert severity='success' sx={{ mb: 2 }} data-testid='relay-fax-now-on-relay-tab'>
            This fax was delivered. The order now appears in the Relay tab.
          </Alert>
        )}

        <NonRoutableRelayFaxSummary request={detail.request} relay={detail.relay} />

        <Typography variant='subtitle2' sx={{ mt: 3 }}>
          Line items
        </Typography>
        {/* Always a fax order here, so the Quello-only script column is dropped. */}
        <RelayLineItemsTable lineItems={detail.lineItems} deliveryMethod='FAX' />

        <Typography variant='subtitle2' sx={{ mt: 3 }}>
          History
        </Typography>
        <RelayEventTimeline events={detail.events} hasMoreEvents={detail.hasMoreEvents} />
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      aria-labelledby='NonRoutableRelayFaxDetailDialog-title'
    >
      <DialogTitle id='NonRoutableRelayFaxDetailDialog-title'>
        Relay fax
        <Typography variant='body2' color='textSecondary'>
          {item.referenceId}
        </Typography>
      </DialogTitle>
      <DialogContent>{renderBody()}</DialogContent>
      <DialogActions>
        <Button variant='outlined' color='secondary' onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NonRoutableRelayFaxDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.shape({
    nonRoutableRequestId: PropTypes.string.isRequired,
    referenceId: PropTypes.string,
  }).isRequired,
};

export default NonRoutableRelayFaxDetailDialog;
